/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Logger } from "@html-core";
import { signal } from "../../shared/common/service/signal";
import { CertService } from "../../shared/base/cert.service";

/**
 * For chrome app, it cannot jump to cert accept page automatically when finding
 * self signed cert. We need this service to do things for us.
 */

export class CertChromeService extends CertService {
   private xhrTimeout: number;

   constructor() {
      super();
      signal.makeObservable(this);
      this.addSignal("loadSrc");
      this.xhrTimeout = 10000;
   }

   /**
    * Send a HEAD request to server to check if it is accessible.
    */
   public isUrlAccessible = (url) => {
      url = "https://" + url.replaceAll("https://", "");
      return new Promise((resolve, reject) => {
         const xhr = new XMLHttpRequest();
         xhr.onreadystatechange = function () {
            // If request is done
            if (this.readyState === 4) {
               // Check the status code
               const status = this.status;
               let responseURL = "";
               if (typeof this.responseURL === "string") {
                  responseURL = this.responseURL.replace("https://", "").split("/")[0];
               }
               if ((status >= 200 && status <= 399) || (status >= 401 && status <= 403) || status === 405) {
                  Logger.debug("Url: '" + url + "' is accessible, http status code is: " + status);
                  resolve({ isUrlAccessible: true, responseURL: responseURL });
               } else {
                  Logger.debug("Url: '" + url + "' is not accessible, http status code is: " + status);
                  resolve({ isUrlAccessible: false, responseURL: responseURL });
               }
            }
         };
         xhr.onerror = function () {
            Logger.debug("Url: '" + url + "' is not accessible, http status code is: " + this.status);
            resolve({ isUrlAccessible: false, responseURL: this.responseURL });
         };
         xhr.timeout = this.xhrTimeout;
         try {
            xhr.open("HEAD", url, true);
            Logger.debug("certService send XHR with tiemout = " + this.xhrTimeout);
            xhr.send();
         } catch (e) {
            Logger.debug("Url: '" + url + "' is not accessible, exception found during connection");
            //@ts-ignore
            resolve({ isUrlAccessible: false, responseURL: this.responseURL });
         }
      });
   };

   public tryToShowCertPage = (url) => {
      this.emit("loadSrc", url);
   };

   public setXHRTimeoutValue = () => {
      /*
       * Enlarge the default XHR timeout value when using smart card authentication.
       * The reason is that.
       *
       *    1. Two way ssl connections take more time than one way ssl connection
       *    2. It take the end user some time to select the certificate and input
       *       the pin code.
       *
       * This change is to fix the multiple certificates windows pop up issues.
       */
      this.xhrTimeout = 50000;
   };
}
